import type {
  ChainPlugin,
  BitcoinTransactionInput,
  DecodedTransaction,
  ValidationResult,
  ValidationError,
  Warning,
} from '@txviz/core';
import { validateNonNegativeBigInt, hexToBytes } from '@txviz/core';
import { serializeBitcoinTransaction } from './serializer.js';
import { hashBitcoinTransaction, reverseBytes, computeLegacySighash, computeSegWitSighash } from './hasher.js';
import { decodeBitcoinTransaction } from './decoder.js';

/**
 * Bitcoin chain plugin.
 *
 * Handles legacy and SegWit transaction verification.
 * TXID is computed as reversed double-SHA256 of the legacy (non-witness) serialization.
 *
 * The hash() method is async because it uses the Web Crypto API's SHA-256,
 * which returns Promises. The ChainPlugin interface supports this via
 * the `Uint8Array | Promise<Uint8Array>` return type.
 */
export const bitcoinPlugin: ChainPlugin<BitcoinTransactionInput> & {
  _lastInput: BitcoinTransactionInput | null;
  _lastSerialized: Uint8Array | null;
} = {
  chain: 'bitcoin',
  _lastInput: null,
  _lastSerialized: null,

  validateInput(input: BitcoinTransactionInput): ValidationResult {
    const errors: ValidationError[] = [];
    const warnings: Warning[] = [];

    // Validate inputs
    if (!input.inputs || input.inputs.length === 0) {
      errors.push({ field: 'inputs', message: 'Transaction must have at least one input' });
    }

    for (let i = 0; i < input.inputs.length; i++) {
      const txInput = input.inputs[i]!;

      // Validate txid (64 hex chars, no 0x prefix)
      const txid = txInput.txid.startsWith('0x') ? txInput.txid.slice(2) : txInput.txid;
      if (!/^[0-9a-fA-F]{64}$/.test(txid)) {
        errors.push({ field: `inputs[${i}].txid`, message: 'Invalid txid — must be 32 bytes hex' });
      }

      if (txInput.vout < 0) {
        errors.push({ field: `inputs[${i}].vout`, message: 'vout must be non-negative' });
      }

      if (txInput.value !== undefined) {
        const err = validateNonNegativeBigInt(txInput.value, `inputs[${i}].value`);
        if (err) errors.push(err);
      }
    }

    // Validate outputs
    if (!input.outputs || input.outputs.length === 0) {
      errors.push({ field: 'outputs', message: 'Transaction must have at least one output' });
    }

    for (let i = 0; i < input.outputs.length; i++) {
      const output = input.outputs[i]!;
      const err = validateNonNegativeBigInt(output.value, `outputs[${i}].value`);
      if (err) errors.push(err);

      const script = output.scriptPubKey.startsWith('0x')
        ? output.scriptPubKey.slice(2)
        : output.scriptPubKey;
      if (!/^[0-9a-fA-F]*$/.test(script) || script.length % 2 !== 0) {
        errors.push({
          field: `outputs[${i}].scriptPubKey`,
          message: 'Invalid hex scriptPubKey',
        });
      }
    }

    // Detect unsigned transaction (empty scriptSigs)
    const hasEmptyScriptSig = input.inputs.some(
      (inp) => !inp.scriptSig || inp.scriptSig.length === 0,
    );
    const hasPrevScriptPubKey = input.inputs.some(
      (inp) => inp.prevScriptPubKey && inp.prevScriptPubKey.length > 0,
    );

    if (hasEmptyScriptSig && hasPrevScriptPubKey) {
      warnings.push({
        message:
          'Unsigned transaction detected — computing sighash (the actual hash you sign) using the provided previous output script.',
        severity: 'info',
      });
    } else if (hasEmptyScriptSig) {
      warnings.push({
        message:
          'This transaction has empty scriptSig(s) — it appears to be unsigned. ' +
          'To verify the sighash (what you actually sign), provide the previous output\'s scriptPubKey. ' +
          'Without it, only the TXID hash of this unsigned transaction can be computed, which will differ from the signed TXID.',
        severity: 'warning',
      });
    }

    // Validate version
    if (input.version !== 1 && input.version !== 2) {
      warnings.push({
        message: `Unusual transaction version: ${input.version}`,
        severity: 'warning',
      });
    }

    // Validate rawHex if provided
    if (input.rawHex !== undefined) {
      const raw = input.rawHex.startsWith('0x') ? input.rawHex.slice(2) : input.rawHex;
      if (!/^[0-9a-fA-F]*$/.test(raw) || raw.length % 2 !== 0) {
        errors.push({ field: 'rawHex', message: 'Invalid hex in rawHex' });
      }
    }

    return { valid: errors.length === 0, errors, warnings };
  },

  serialize(input: BitcoinTransactionInput): Uint8Array {
    this._lastInput = input;
    const serialized = serializeBitcoinTransaction(input);
    this._lastSerialized = serialized;
    return serialized;
  },

  async hash(serialized: Uint8Array): Promise<Uint8Array> {
    const input = this._lastInput;

    // Check if this is an unsigned tx with UTXO data available.
    // If so, compute the sighash (what the user actually signs) instead of the TXID.
    if (input) {
      const isUnsigned = input.inputs.some(
        (inp) => !inp.scriptSig || inp.scriptSig.length === 0,
      );
      const hasPrevScriptPubKey = input.inputs.some(
        (inp) => inp.prevScriptPubKey && inp.prevScriptPubKey.length > 0,
      );

      if (isUnsigned && hasPrevScriptPubKey) {
        // Compute sighash for the first input that has prevScriptPubKey.
        for (let i = 0; i < input.inputs.length; i++) {
          const inp = input.inputs[i]!;
          if (inp.prevScriptPubKey && inp.prevScriptPubKey.length > 0) {
            const prevScript = hexToBytes(
              inp.prevScriptPubKey.startsWith('0x')
                ? inp.prevScriptPubKey
                : `0x${inp.prevScriptPubKey}`,
            );

            // Detect SegWit (P2WPKH: 0014..., P2WSH: 0020...) vs Legacy
            const isSegWitScript =
              (prevScript.length === 22 && prevScript[0] === 0x00 && prevScript[1] === 0x14) ||
              (prevScript.length === 34 && prevScript[0] === 0x00 && prevScript[1] === 0x20);

            if (isSegWitScript && inp.value !== undefined) {
              // BIP-143 SegWit sighash — needs the UTXO value
              return computeSegWitSighash(serialized, i, prevScript, inp.value);
            } else {
              // Legacy sighash
              return computeLegacySighash(serialized, i, prevScript);
            }
          }
        }
      }
    }

    // Default: compute TXID hash (reversed double-SHA256)
    const hash = await hashBitcoinTransaction(serialized);
    return reverseBytes(hash);
  },

  async decode(input: BitcoinTransactionInput): Promise<DecodedTransaction> {
    return decodeBitcoinTransaction(input);
  },
};
